import { useEffect, useState } from 'react'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'

import styles from './NewAppointmentCard.module.sass'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import SlidingText from '@jimmyboys/liita-ui/dist/components/SlidingText'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import AppointmentCardActions from './AppointmentCardActions/AppointmentCardActions'
import AppointmentCardSmall from './AppointmentCardSmall/AppointmentCardSmall'
import { useInbox } from 'store'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
	currentUserProcessPreferences,
	expandedInboxWithSizeMediumState,
} from 'store/atoms'
import AppointmentComments from './AppointmentComments/AppointmentComments'
import AppointmentRequisitions from './AppointmentRequisitions/AppointmentRequisitions'
import AppointmentDocuments from './AppointmentDocuments/AppointmentDocuments'
import AppointmentHistory from './AppointmentHistory/AppointmentHistory'
import AppointmentDisabilitiesHistory from './AppointmentDisabilitiesHistory/AppointmentDisabilitiesHistory'
import FuseHighlight from 'components/FuseHighlight/FuseHighlight'
import getMedicalSpecialtyClass from 'utils/getMedicalSpecialty'
import Popover from '@jimmyboys/liita-ui/dist/components/Popover'

const displayDate = (data) => {
	return data?.scheduled ? (
		<HStack className={styles['content-date']}>
			<h5>{data?.date?.shortDateFormat}</h5>
			<small>{data?.date?.shortTimeFormat}</small>
		</HStack>
	) : (
		<HStack className={styles['content-date']}>
			<h5>{data?.$cardLabels.unscheduledAppointment}</h5>
		</HStack>
	)
}

const NewAppointmentCard = (props) => {
	const { screenSize } = useGlobal()
	const { expandedInbox } = useInbox()
	const { className, data, ...attributes } = props
	const [expanded, setExpanded] = useState(false)
	const [showHistory, setShowHistory] = useState(false)
	const [showAppointmentHistory, setShowAppointmentHistory] = useState(false)
	const [expandedInboxMedium, setExpandedInboxMedium] = useRecoilState(
		expandedInboxWithSizeMediumState
	)
	const processPreferences = useRecoilValue(currentUserProcessPreferences)

	useEffect(() => {
		setExpandedInboxMedium(
			() => screenSize === 'medium' && expandedInbox === true
		)
	}, [expandedInbox, screenSize, setExpandedInboxMedium])

	useEffect(() => {
		setExpanded(processPreferences?.unfolded)
	}, [processPreferences?.unfolded])

	const hasRequisitions = data?.requisitions && data.requisitions.length > 0
	const hasComments = data?.comments && data.comments.length > 0

	const hasPreviousAppointment =
		data?.previousAppointment?.id || data?.previousAppointment?.specialty

	const isCancelled = data?.status?.value === 'cancelled'
	const isRemarked = data?.status?.value === 'remarked'

	const showAlerts =
		hasRequisitions ||
		data.hasDocuments ||
		isCancelled ||
		isRemarked ||
		hasComments

	const content = isRemarked ? (
		<>
			<h5>{data?.$cardLabels.nonExecutionMotive}</h5>
			<small>
				{data.nonExecutionMotive ||
					data?.$cardLabels.withoutObservations}
			</small>
		</>
	) : (
		<>
			<h5>{data?.$cardLabels.cancelMotive}</h5>
			<small>
				{data.cancelMotive || data?.$cardLabels.withoutObservations}
			</small>
		</>
	)

	return (
		<>
			{!expandedInboxMedium && screenSize !== 'small' ? (
				<div
					{...attributes}
					className={`${styles['root'] || ''} ${className || ''}`}
				>
					<HStack
						className={`${styles['appointment-card']} ${
							expanded && styles['--expanded']
						} ${data?.$highlight ? styles['highlighted'] : ''}`}
					>
						<div
							className={`${styles['appointment-specialty']} ${
								styles[
									`--${getMedicalSpecialtyClass(
										data?.medicalSpecialty?.id
									)}-color`
								]
							}`}
						/>
						{showAlerts ? (
							<div className={`${styles['appointment-alerts']}`}>
								{hasComments && (
									<Icon
										name='chat-bubble'
										width='20px'
										height='20px'
									/>
								)}
								{hasRequisitions && (
									<Icon
										name='requisition'
										width='20px'
										height='20px'
									/>
								)}
								{data.hasDocuments && (
									<Icon
										name='clip'
										width='20px'
										height='20px'
									/>
								)}
								{(isCancelled || isRemarked) && (
									<Popover content={content}>
										<Icon
											className={styles['icon-hover']}
											name='error-info'
											width='20px'
											height='20px'
										/>
									</Popover>
								)}
							</div>
						) : null}
						<VStack
							className={`${styles['card-content-details']} ${
								expanded && styles['--expanded']
							}`}
						>
							<div
								className={`${styles['content-header']} ${
									expanded &&
									((showHistory &&
										data?.history &&
										data?.history?.length > 0) ||
										data?.hasDocuments ||
										(data?.requisitions &&
											data?.requisitions.length > 0))
										? styles['--expanded']
										: ''
								}`}
							>
								<VStack
									className={
										styles['appointment-specialty-doctor']
									}
								>
									<h5>{data?.medicalSpecialty?.name}</h5>
									<small>{data?.doctor?.name}</small>
								</VStack>
								{expanded &&
								data?.previousAppointment?.id !== data?.id &&
								hasPreviousAppointment ? (
									<HStack
										className={
											styles['previous-appointment']
										}
									>
										<Icon
											className={
												styles[
													'subdirectory-arrow-icon'
												]
											}
											name='subdirectory-arrow'
											width='20px'
											height='20px'
										></Icon>
										<VStack>
											<small>
												{data?.previousAppointment
													?.specialty || '-'}
											</small>
											<small>
												#
												{data?.previousAppointment
													?.id || ' -'}
											</small>
										</VStack>
									</HStack>
								) : (
									''
								)}
								{displayDate(data)}
								<VStack>
									<SlidingText
										className={styles['content-location']}
									>
										<strong>
											{data?.establishment?.name}
										</strong>
									</SlidingText>
									<SlidingText>
										<small>{data?.transport}</small>
									</SlidingText>
								</VStack>
							</div>
							{expanded && (
								<>
									{data?.requisitions.length ? (
										<AppointmentRequisitions data={data} />
									) : (
										''
									)}
									{data?.comments && data?.comments.length ? (
										<AppointmentComments data={data} />
									) : (
										''
									)}
									{data?.hasDocuments ? (
										<AppointmentDocuments data={data} />
									) : (
										''
									)}
									{showHistory && data?.history ? (
										<AppointmentHistory data={data} />
									) : (
										''
									)}
									{showAppointmentHistory ? (
										<AppointmentDisabilitiesHistory
											appointmentId={data.id}
											processId={data.processId}
										/>
									) : null}
								</>
							)}
						</VStack>
						<HStack className={styles['content-observation']}>
							<div
								className={styles['card-observation-container']}
							>
								<div
									className={`${styles['card-observation']} ${
										expanded && styles['--expanded']
									} `}
								>
									<span>
										<FuseHighlight
											matchedWords={
												data.$textHighlight
													?.matchedIndexes?.[
													'medicalObservation'
												]
											}
											text={data.medicalObservation}
										/>
									</span>
								</div>
								<button
									className={styles['read-more']}
									onClick={() => setExpanded(!expanded)}
								>
									{expanded ? (
										<h5>{data?.$cardLabels.seeLess}</h5>
									) : (
										<h5>{data?.$cardLabels.seeMore}</h5>
									)}
								</button>
							</div>
							{data?.disability?.id ? (
								<div className={styles['ita']}>
									<h5>{data?.disability?.id}</h5>
									<small>
										{data?.disability?.disabilityPercentage}
									</small>
								</div>
							) : null}
						</HStack>
						<AppointmentCardActions
							data={data}
							setExpanded={setExpanded}
							showHistory={showHistory}
							setShowHistory={setShowHistory}
							showAppointmentHistory={showAppointmentHistory}
							setShowAppointmentHistory={
								setShowAppointmentHistory
							}
						/>
					</HStack>
				</div>
			) : (
				<AppointmentCardSmall
					hasRequisitions={hasRequisitions}
					expanded={expanded}
					data={data}
					showHistory={showHistory}
					setExpanded={setExpanded}
					setShowHistory={setShowHistory}
				/>
			)}
		</>
	)
}
export default NewAppointmentCard
